// ═══════════════════════════════════════════════════════════
// 商业化模块类型定义
// 支付（Payment）相关
// ═══════════════════════════════════════════════════════════

import type {SubscriptionType, SkuId, OrderId} from '../type';

/** 订单类型 */
export type OrderType = 'membership' | 'points';

/** 支付方式 */
export type PayMethod = 'Alipay' | 'WeChatPay';

/** 订单状态 */
export const OrderStatusValue = {
    /** 待支付 */
    PENDING: 1,
    /** 已支付 */
    PAID: 2,
    /** 支付失败 */
    FAILED: 3,
    /** 已退款 */
    REFUNDED: 4,
    /** 权益已下发 */
    RIGHTS_ISSUED: 5,
} as const;

export type OrderStatusType = (typeof OrderStatusValue)[keyof typeof OrderStatusValue];

/** 通用订单（业务无关） */
export interface Order {
    /** 业务订单号（服务端生成） */
    orderId: OrderId;
    /** 订单类型：1=会员、2=积分、3=升级补差价 */
    orderType: OrderType;
    /** 订阅类型（会员订阅有，积分无） */
    subscriptionType?: SubscriptionType;
    /** 实付金额（单位：分） */
    payAmount: number;
    /** 订单状态 */
    orderStatus: OrderStatusType;
    /** SKU 描述 */
    sku: {
        /** SKU 商品 ID */
        skuId: SkuId;
        /** 商品名字，如 "旗舰版·连续包月" / "5000 积分" */
        skuName: string;
        /** 积分是否立即生效：
         * true=本次已立即发放，展示「xxx积分已发放」；
         * false=随订阅生效后发放，展示「xxx积分随订阅生效后发放」
         * */
        pointsIsImmediate: boolean;
        /** 本次发放/到账积分 */
        points: number;
        /** 会员订阅延迟生效时间戳(Unix 秒) */
        skuStartAt?: number;
        /** 会员到期时间戳(Unix 秒) */
        skuExpireAt?: number;
    };
    /** 支付二维码信息 */
    qrCode: {
        /** 支付协议字符串（聚合支付，前端本地渲染二维码） */
        payload: string;
        /** 二维码支付方式：Alipay-支付宝支付，WeChatPay-微信支付 */
        payMethods: PayMethod[];
        /** 二维码过期时间戳(Unix 秒)（服务端下发，避免前端时钟不准） */
        expireAt: number;
    };
}

/** 轮询接口响应（与 Order 结构一致，但不含 qrCode） */
export type OrderPollingResult = Omit<Order, 'qrCode'>;

/** 业务侧下单时的输入 */
export type SkuInput =
    | {orderType: 'membership'; skuId: SkuId}
    | {orderType: 'points'; skuId: SkuId};

/** 支付价格校验请求参数 */
export interface PaymentCheckReqParams {
    /** 套餐id */
    skuId: SkuId;
    /** 当前显示支付金额 */
    payAmount: number;
}

/** 支付价格校验结果 */
export interface PaymentCheckResult {
    /** 价格是否一致 */
    pass: boolean;
    /** 服务端金额 */
    latestPayAmount: number;
}
